import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from "react"
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import {
    Col,
    Row,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    Label,
    Input,
    Spinner,
} from "reactstrap";

import {
    getAllCustomers,
    getToysGrouped,
    getCities,
} from "store/actions";
import Select from "react-select"
import { createSelector } from "reselect";
import { get, post } from 'helpers/api_helper';
import moment from 'moment';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';


const AddOrderModal = ({ show, getAllOrders, onCloseClick }) => {
    const dispatch = useDispatch();
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [selectedToy, setSelectedToy] = useState(null);
    const [selectedToyUnit, setSelectedToyUnit] = useState(null);
    const [selectedDuration, setSelectedDuration] = useState({
        value: 2,
        label: "2 weeks",
    });
    const [toyAvailabilty, setToyAvailabilty] = useState(null);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
    const [rent, setRent] = useState(null);
    const [deliveryFee, setDeliveryFee] = useState(null);
    const [pickupPointSelected, setPickupPointSelected] = useState(false);
    const [pickupPoint, setPickupPoint] = useState(null);
    const [pickupPoints, setPickupPoints] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCity, setSelectedCity] = useState(null);
    const [plan, setPlan] = useState(null);
    const [useSubscription, setUseSubscription] = useState(false);
    const selectCityState = (state) => state.City;

    const CityProperties = createSelector(selectCityState, (City) => ({
        cities: City.cities,
    }));

    const { cities } = useSelector(CityProperties);

    const resetStates = () => {
        setSelectedToy(null);
        setSelectedToyUnit(null);
        setQuery("");
        setSelectedDuration(null);
        setToyAvailabilty(null);
        setSelectedTimeSlot(null);
        setRent(null);
        setPickupPointSelected(false);
        setPickupPoint(null);
        setPickupPoints([]);
    }

    const handleCityChange = (selectedOption) => {
        setSelectedCity(selectedOption);
        setSelectedCustomer(null);
        setSelectedEmail(null);
        resetStates();
    };

    const handleCustomerChange = (selectedOption) => {
        const customer = allCustomers.find(c => c._id === selectedOption.value);
        setSelectedCustomer({ value: customer._id, label: `${customer.fname} ${customer.lname}`, customer: customer });
        setSelectedEmail({ value: customer._id, label: customer.email });
        resetStates();
        if (selectedCity?.value) {
            dispatch(getToysGrouped(1, 1000, "", "all", "all", selectedCity.value, "all", "all"));
        }
    };

    const handleEmailChange = (selectedOption) => {
        const customer = allCustomers.find(c => c._id === selectedOption.value);
        setSelectedCustomer({ value: customer._id, label: `${customer.fname} ${customer.lname}`, customer: customer });
        setSelectedEmail({ value: customer._id, label: customer.email });
        setPickupPointSelected(false);
        resetStates();
    };

    const calculateRent = () => {
        if (!selectedCustomer || !selectedToy || !selectedDuration) {
            setRent(null);
            return;
        }

        const { cityPricing, deliveryFee } = selectedCustomer;
        if (!cityPricing) {
            setRent("Toy Not Available in this city");
            return;
        }

        let calculatedRent = 0;
        switch (selectedDuration.value) {
            case 2:
                calculatedRent = cityPricing.w2;
                break;
            case 3:
                calculatedRent = cityPricing.w3;
                break;
            case 4:
                calculatedRent = cityPricing.w4;
                break;
            default:
                calculatedRent = 0;
                break;
        }
        setRent(calculatedRent);
        setDeliveryFee(deliveryFee || 0);
    };

    const selectTimeSlot = (slot) => {
        setSelectedTimeSlot(slot);
    };

    useEffect(() => {
        calculateRent();
    }, [selectedDuration]);

    const handleToyChange = async (selectedOption) => {
        setSelectedToy(selectedOption);
        setSelectedToyUnit(null);
        const toy = selectedOption?.value;
        const customerId = selectedCustomer?.value;
        const customer = allCustomers.find(c => c._id === customerId);
        const customerCity = customer?.city.name;
        const toyDetails = toys.find(t => t._id === toy);
        const cityPricing = toyDetails?.cityPricing.find(cp => cp.city.name === customerCity);

        if (!cityPricing) {
            setRent(null);
            return;
        }

        setSelectedCustomer((prev) => ({
            ...prev,
            cityPricing,
            deliveryFee: customer?.zone?.deliveryFee || 0
        }));
    };

    const getToyAvailability = async (toy, customerId, toyUnitId) => {
        setLoading(true);
        try {
            const pickupPoint = pickupPointSelected;
            const res = await post(`/toys/getAvailability`, { toy, customerId, pickupPoint, duration: selectedDuration?.value, toyUnitId });
            if (res.success === false) {
                toast.error(res.message);
                return;
            }
            const { availability } = res;
            setToyAvailabilty(availability);
            setSelectedTimeSlot(null);
        } catch (error) {
            console.log("Failed to get toy availability", error);
        }
        setLoading(false);
    }

    useEffect(() => {
        if (selectedToy && selectedCustomer && selectedDuration) {
            getToyAvailability(selectedToy?.value, selectedCustomer?.value, selectedToyUnit?.value);
        }
    }, [selectedDuration, selectedToy, pickupPointSelected, selectedToyUnit]);

    const handleDurationChange = (selectedOption) => {
        setSelectedDuration(selectedOption);
        calculateRent();
    };

    const Duration = [2, 3, 4]; //Duration in weeks
    //Select tag CSS:
    const customStyles = {
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f1f1f1' : null,
            color: state.isSelected ? '#fff' : '#333',
            padding: 10,
        }),
        control: (provided) => ({
            ...provided,
            border: '1px solid #ced4da',
            borderRadius: '4px',
            boxShadow: 'none',
            '&:hover': {
                border: '1px solid #007bff',
            },
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
        }),
    };

    const selectCustomerState = (state) => state.Customer;
    const ExistingCustomers = createSelector(
        selectCustomerState,
        (Customer) => ({
            allCustomers: Customer.allCustomers,
        })
    );
    const { allCustomers } = useSelector(ExistingCustomers);

    const selectToyState = (state) => state.Toy;
    const ToyProperties = createSelector(
        selectToyState,
        (Toy) => ({
            toys: Toy.toys
        })
    );
    const { toys } = useSelector(ToyProperties);


    const handleSave = async () => {
        if (!selectedCustomer || !selectedToy || !selectedToyUnit || !selectedDuration || !selectedTimeSlot) {
            toast.error("Please fill all the fields");
            return;
        }

        if (pickupPointSelected && (!pickupPoint || !pickupPoint.value)) {
            toast.error("Please select a pickup point");
            return;
        }

        if (useSubscription && subscriptionProblem) {
            toast.error(subscriptionProblem);
            return;
        }

        if (!toyAvailabilty[`w${selectedDuration.value}`].includes(selectedTimeSlot)) {
            toast.error("Toy not available for the selected time slot");
            return;
        }

        const newOrder = {
            customerId: selectedCustomer?.value,
            toyId: selectedToyUnit?.value,
            duration: selectedDuration?.value,
            deliveryDate: new Date(selectedTimeSlot).toISOString(),
            selfPickup: pickupPointSelected ? pickupPoint?.value : null,
            ...(useSubscription ? { useSubscription: true } : {}),
        };

        setLoading(true);
        try {
            const res = await post(`orders/add`, newOrder);
            toast.success("Order added successfully");
            getAllOrders();
            closeModal();
        } catch (error) {
            console.log("Failed to add order", error);
            let msg = error?.response?.data?.message || "Failed to add order";
            toast.error(msg);
        }
        setLoading(false);
    };


    const [query, setQuery] = useState("");
    const [searchType, setSearchType] = useState('name');
    const handleSearchTypeChange = (event) => {
        setSearchType(event.target.value);
    };

    useEffect(() => {
        if (selectedCity?.value) {
            dispatch(getAllCustomers(query || "", searchType, selectedCity.value));
        }
    }, [query, searchType, selectedCity, dispatch]);

    const debouncedSetQuery = useCallback(
        debounce((inputValue) => {
            setQuery(inputValue);
        }, 300),
        []
    );

    const fetchPickUpPoints = async () => {
        try {
            const res = await get(`orders/pickup-points/${selectedCustomer?.value}`);
            setPickupPoints(res?.pickUpPoints);
        } catch (error) {
            console.log("Error in fetching pick up points", error);
        }
    }

    useEffect(() => {
        if (selectedCustomer) {
            fetchPickUpPoints();
        }
    }, [selectedCustomer]);

    useEffect(() => {
        setPlan(null);
        setUseSubscription(false);
        const customerId = selectedCustomer?.value;
        if (!customerId) return;
        let active = true;
        get(`subscriptions/customer/${customerId}/usage`)
            .then((res) => active && setPlan(res?.plan || null))
            .catch(() => active && setPlan(null));
        return () => {
            active = false;
        };
    }, [selectedCustomer?.value]);

    // Toy's 2-week rent in the customer's city (city may be an id or a populated object).
    const customerCity = selectedCustomer?.customer?.city;
    const customerCityId = String(customerCity?._id || customerCity || "");
    const toyCityPrice =
        selectedToy?.toy?.cityPricing?.find((cp) => {
            const cpCityId = String(cp?.city?._id || cp?.city || "");
            return (customerCityId && cpCityId === customerCityId) || (customerCity?.name && cp?.city?.name === customerCity.name);
        }) || selectedCustomer?.cityPricing;
    const twoWeekRent = Number(toyCityPrice?.w2) || 0;
    const planRangeFrom = Number(plan?.orderRange?.from || 0);
    const planRangeTo = plan?.orderRange?.to == null ? null : Number(plan.orderRange.to);
    const toyInPlan = !!plan && twoWeekRent > 0 && twoWeekRent >= planRangeFrom && (planRangeTo == null || twoWeekRent <= planRangeTo);
    // Offer the subscription only for a picked toy that is within the pack range.
    const canUseSubscription = !!plan && !!selectedToy && toyInPlan;
    useEffect(() => {
        if (!canUseSubscription) setUseSubscription(false);
    }, [canUseSubscription]);
    const turnsNeeded = selectedDuration?.value ? Number(selectedDuration.value) / 2 : 0;
    const planHasTurns = !!plan && turnsNeeded <= Number(plan.turnsAvailable || 0);
    const subscriptionProblem = !useSubscription
        ? ""
        : selectedToy && twoWeekRent > 0 && !toyInPlan
            ? "This toy is not included in the customer's subscription plan."
            : selectedDuration && !planHasTurns
                ? `Not enough turns left (${plan?.turnsAvailable ?? 0} available, ${turnsNeeded} needed).`
                : "";

    useEffect(() => {
        handleToyChange(selectedToy);
    }, [pickupPointSelected]);

    const closeModal = () => {
        onCloseClick();
        setSelectedCity(null);
        setSelectedCustomer(null);
        setSelectedEmail(null);
        resetStates();
    };

    const availableUnits = selectedToy?.toy?.variants?.filter(v => v.status !== "BROKEN" && v.status !== "MAINTENANCE") || [];
    const toyUnitOptions = availableUnits.map(unit => ({ value: unit._id, label: unit.code, unit }));

    return (
        <Modal size="lg" isOpen={show} toggle={closeModal} centered={true}>
            <ModalHeader toggle={closeModal}>Add Orde</ModalHeader>
            <ModalBody>
                <Form>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Label>City:</Label>
                            <Select
                                styles={customStyles}
                                value={selectedCity}
                                onChange={handleCityChange}
                                options={cities.filter(city => city.status === true).map(city => ({ value: city._id, label: city.name }))}
                                placeholder="Select City"
                            />
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={6} className=' d-flex gap-2 align-items-center'>
                            <Label>Search By:</Label>
                            <div className=' d-flex gap-4'>
                                <label htmlFor="searchByName" style={{ cursor: "pointer" }} className=' d-flex gap-1 align-items-center'>
                                    <input
                                        type="radio"
                                        id="searchByName"
                                        name="searchType"
                                        value="name"
                                        checked={searchType === 'name'}
                                        onClick={handleSearchTypeChange}
                                        style={{ cursor: "pointer" }}
                                    /> Name
                                </label>
                                <label htmlFor="searchByEmail" style={{ cursor: "pointer" }} className=' d-flex gap-1 align-items-center'>
                                    <input
                                        type="radio"
                                        id="searchByEmail"
                                        name="searchType"
                                        value="email"
                                        checked={searchType === 'email'}
                                        onClick={handleSearchTypeChange}
                                        style={{ cursor: "pointer" }}
                                    /> Email
                                </label>
                            </div>
                        </Col>
                        {
                            searchType === "name" ?
                                <Col md={6}>
                                    <Label>Customer:</Label>
                                    <Select
                                        styles={customStyles}
                                        value={selectedCustomer}
                                        onChange={handleCustomerChange}
                                        options={allCustomers.map((customer) => ({ value: customer?._id, label: customer?.fname + " " + customer?.lname }))}
                                        placeholder="Select Customer"
                                        onInputChange={debouncedSetQuery}
                                    />
                                </Col>
                                :
                                <Col md={6}>
                                    <Label>Email:</Label>
                                    <Select
                                        styles={customStyles}
                                        value={selectedEmail}
                                        onChange={handleEmailChange}
                                        options={allCustomers.map((customer) => ({ value: customer?._id, label: customer?.email }))}
                                        placeholder="Select Email"
                                        onInputChange={debouncedSetQuery}
                                    />
                                </Col>
                        }
                    </Row>
                    <Row className="mb-3">
                        {
                            selectedCustomer && (
                                <Col md={12}>
                                    <Label>
                                        Selected Customer :
                                    </Label>
                                    <div className='border border-1 border-primary rounded-3 p-2 d-flex flex-column flex-md-row'>
                                        <Col md={6} className=' d-flex flex-column'>
                                            <Label><strong>Name:</strong> {selectedCustomer?.customer?.fname} {selectedCustomer?.customer?.lname}</Label>
                                            <Label><strong>Email:</strong> {selectedCustomer?.customer?.email}</Label>
                                            <Label><strong>Mobile:</strong> {selectedCustomer?.customer?.mobile}</Label>
                                        </Col>
                                        <Col md={6} className=' d-flex flex-column'>
                                            <Label><strong>City:</strong> {selectedCustomer?.customer?.city?.name}</Label>
                                            <Label><strong>Zone:</strong> {selectedCustomer?.customer?.zone?.name}
                                                <span style={{ fontStyle: "italic" }} className=' text-secondary'>
                                                    {" (Home Delivery: "}{selectedCustomer?.customer?.zone?.homeDelivery ?
                                                        <i className="fas fa-check text-success"></i> :
                                                        <i className="fas fa-times text-danger"></i>} {")"}
                                                </span>
                                            </Label>
                                            <Label><strong>Pincode:</strong> {selectedCustomer?.customer?.pincode}</Label>
                                        </Col>
                                    </div>
                                </Col>
                            )
                        }
                    </Row>
                    <Row className="mb-3">
                        <Col md={4}>
                            <Label>Toy:</Label>
                            <Select
                                styles={customStyles}
                                value={selectedToy}
                                onChange={handleToyChange}
                                options={toys.map(toy => ({ value: toy._id, label: toy.name, toy }))}
                                placeholder="Select Toy"
                                isDisabled={!selectedCustomer && !selectedEmail}
                            />
                        </Col>
                        <Col md={4}>
                            <Label>Toy Unit:</Label>
                            <Select
                                styles={customStyles}
                                value={selectedToyUnit}
                                onChange={(selectedOption) => setSelectedToyUnit(selectedOption)}
                                options={toyUnitOptions}
                                placeholder={selectedToy ? (availableUnits.length > 0 ? "Select Toy Unit" : "No units available now") : "Select Toy first"}
                                isDisabled={!selectedToy || availableUnits.length === 0}
                            />
                        </Col>
                        <Col md={4}>
                            <Label>Duration:</Label>
                            <Select
                                styles={customStyles}
                                value={selectedDuration}
                                onChange={handleDurationChange}
                                options={Duration.map((duration) => ({ value: duration, label: duration + " weeks" }))}
                                placeholder="Select Duration (weeks)"
                                isDisabled={!selectedToyUnit}
                            />
                        </Col>
                    </Row>
                    {canUseSubscription && (
                        <Row className="mb-3">
                            <Col md={12}>
                                <Label className="d-block">Book As:</Label>
                                <div className="d-flex flex-wrap gap-3">
                                    <div
                                        role="radio"
                                        aria-checked={!useSubscription}
                                        tabIndex={0}
                                        onClick={() => setUseSubscription(false)}
                                        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setUseSubscription(false))}
                                        className={`flex-fill d-flex align-items-center gap-2 rounded-3 p-2 px-3 border ${!useSubscription ? "border-2 border-primary bg-primary bg-opacity-10" : "border-1"}`}
                                        style={{ cursor: "pointer", minWidth: 220 }}
                                    >
                                        <i className={`font-size-20 mdi ${!useSubscription ? "mdi-radiobox-marked text-primary" : "mdi-radiobox-blank text-muted"}`} />
                                        <div>
                                            <div className="fw-semibold">Flexible Rental</div>
                                            <div className="text-muted font-size-12">Normal rent for the selected weeks</div>
                                        </div>
                                    </div>
                                    <div
                                        role="radio"
                                        aria-checked={useSubscription}
                                        tabIndex={0}
                                        onClick={() => setUseSubscription(true)}
                                        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setUseSubscription(true))}
                                        className={`flex-fill d-flex align-items-center gap-2 rounded-3 p-2 px-3 border ${useSubscription ? "border-2 border-success bg-success bg-opacity-10" : "border-1"}`}
                                        style={{ cursor: "pointer", minWidth: 220 }}
                                    >
                                        <i className={`font-size-20 mdi ${useSubscription ? "mdi-radiobox-marked text-success" : "mdi-radiobox-blank text-muted"}`} />
                                        <div>
                                            <div className="fw-semibold">Subscription — {plan.packageName}</div>
                                            <div className="text-muted font-size-12">₹0 rent · {plan.turnsAvailable} turns left</div>
                                        </div>
                                    </div>
                                </div>
                                {subscriptionProblem && (
                                    <div className="text-danger font-size-12 mt-1">{subscriptionProblem}</div>
                                )}
                            </Col>
                        </Row>
                    )}
                    <Row className="mb-4">
                        {
                            selectedToy && (
                                <Col className="md-6">
                                    <div className="d-flex gap-2">
                                        <Input className="pe-auto" type="checkbox" id="selfPickup" checked={pickupPointSelected}
                                            onClick={(e) => {
                                                setPickupPointSelected((prev) => !prev);
                                                setSelectedTimeSlot(null);
                                                setPickupPoint(null);
                                            }}
                                        />
                                        <Label for="selfPickup" className="pe-auto">Self PickUp from Store/Centre</Label>
                                    </div>
                                </Col>
                            )
                        }
                        {
                            pickupPointSelected && (
                                <Col md="6">
                                    <Select
                                        styles={customStyles}
                                        placeholder="Select Pickup Point"
                                        options={pickupPoints.map((point) => {
                                            return {
                                                value: point._id,
                                                label: point.storeName,
                                            };
                                        })}
                                        value={pickupPoint}
                                        onChange={(e) => {
                                            setPickupPoint({
                                                value: e.value,
                                                label: e.label,
                                            });
                                        }}
                                    />
                                </Col>
                            )

                        }
                    </Row>
                    {
                        selectedToy && selectedDuration &&
                        <Row >
                            <Col md="12">
                                <Label>Delivery Slots:</Label>
                                {
                                    loading ?
                                        <div className=' my-4'>
                                            <Spinner color="primary" className='position-absolute top-50 start-50' />
                                        </div>
                                        :
                                        <div className="row gap-2 mx-auto">
                                            {
                                                toyAvailabilty && toyAvailabilty[`w${selectedDuration.value}`] && toyAvailabilty[`w${selectedDuration.value}`].length > 0 ? (
                                                    toyAvailabilty[`w${selectedDuration.value}`].map((item, index) => (
                                                        <button
                                                            key={index}
                                                            type="button"
                                                            onClick={() => selectTimeSlot(item)}
                                                            // variant={item == selectedTimeSlot ? 'primary' : 'outline-secondary'}
                                                            className={`col-2 border border-1 border-primary rounded-3 p-2 ${item === selectedTimeSlot ? "bg-primary text-white" : ""}`}
                                                        >
                                                            <div>{moment(item).format("ddd")}</div>
                                                            <div>{moment(item).format("DD MMM")}</div>
                                                        </button>
                                                    ))
                                                ) : (
                                                    <div className=" border border-1 border-danger rounded-3 p-2">
                                                        <span className="text-danger"><strong >Delivery Slots Not Available!</strong> <br />Slots not available for selected duration for the toy.Our pre-booking slots open 60 days before.</span>
                                                    </div>
                                                )
                                            }
                                        </div>
                                }
                            </Col>
                            {
                                selectedTimeSlot &&
                                <Col md={6} className=' mt-4 d-flex flex-column'>
                                    {useSubscription ? (
                                        <>
                                            <Label><strong>Rent:</strong> ₹ 0 (Subscription · {turnsNeeded} {turnsNeeded === 1 ? "turn" : "turns"})</Label>
                                        </>
                                    ) : (
                                        <Label><strong>Rent:</strong> {rent !== null ? `₹ ${rent} (for ${selectedDuration?.label})` : "Select Toy and Duration."}</Label>
                                    )}
                                    {
                                        deliveryFee !== null && <Label><strong>Delivery Fee:</strong> ₹ {
                                            pickupPointSelected ? 0 : deliveryFee
                                        }</Label>
                                    }
                                </Col>
                            }
                        </Row>
                    }
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={closeModal}>Cancel</Button>
                <Button color="primary" onClick={handleSave} disabled={loading}>Save</Button>
            </ModalFooter>
        </Modal>
    )
}

AddOrderModal.propTypes = {
    show: PropTypes.bool,
    getAllOrders: PropTypes.func,
    onCloseClick: PropTypes.func,
}

export default AddOrderModal
